/**
 * Track presentation helpers (spec 03): auto-name (nb-NO weekday + date) and a
 * normalized thumbnail path for the list view. Pure — the web layer renders SVG.
 */
import type { TrackPoint } from './polyline.js';

const WEEKDAYS_NB = [
  'Søndag',
  'Mandag',
  'Tirsdag',
  'Onsdag',
  'Torsdag',
  'Fredag',
  'Lørdag',
];
const MONTHS_NB = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
];

/** v1 auto-name: "Søndag 6. juli" (spec 03). */
export function autoTrackName(startedAt: number): string {
  const d = new Date(startedAt);
  const weekday = WEEKDAYS_NB[d.getDay()] ?? '';
  const day = d.getDate();
  const month = MONTHS_NB[d.getMonth()] ?? '';
  return `${weekday} ${day}. ${month}`.trim();
}

/** Time-of-day label, available for later naming refinement. */
export function timeOfDayLabel(startedAt: number): string {
  const h = new Date(startedAt).getHours();
  if (h < 6) return 'Nattseilas';
  if (h < 11) return 'Morgentur';
  if (h < 17) return 'Dagstur';
  if (h < 22) return 'Kveldstur';
  return 'Nattseilas';
}

export interface ThumbPoint {
  x: number;
  y: number;
}

/**
 * Downsample to ~n points and project into a 0..1 box (y flipped for screen).
 * Returns [] for degenerate tracks. Stored on trackMeta at finalize time so the
 * list never touches the point blob.
 */
export function thumbnailPath(points: TrackPoint[], n = 32): ThumbPoint[] {
  if (points.length < 2) return [];

  const step = Math.max(1, Math.floor(points.length / n));
  const sampled: TrackPoint[] = [];
  for (let i = 0; i < points.length; i += step) sampled.push(points[i]!);
  const last = points[points.length - 1]!;
  if (sampled[sampled.length - 1] !== last) sampled.push(last);

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const p of sampled) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }

  // Preserve aspect roughly by correcting longitude for latitude.
  const latSpan = maxLat - minLat || 1e-9;
  const midLatRad = ((minLat + maxLat) / 2) * (Math.PI / 180);
  const lonSpan = (maxLon - minLon) * Math.cos(midLatRad) || 1e-9;
  const span = Math.max(latSpan, lonSpan);

  return sampled.map((p) => ({
    x: round3(((p.lon - minLon) * Math.cos(midLatRad)) / span),
    y: round3(1 - (p.lat - minLat) / span),
  }));
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
